/**
 * @file Allows user to specify a vehicle which is then saved and can be used to filter products across the site.
 * This is similar to the "My Garage" functionality from Supercheap Auto's site.
 * 
 * @author Mark Liston <mark@trundlesautomotive.com>
 * @version 1
 */

// Is populated with all vehicles we support--from the "Vehicle" metaobject.
let myVehicleSearchList = {};

function normaliseStr(str) {
  return String(str)
    .normalize("NFD") // Normalize accented letters.
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritic chars.
    .replace(/[^\p{L}\p{N}]/gu, "") // Remove non letters/numbers (symbols and punctuation).
    .replace(/facelift/gi, "fl")
    .toLowerCase();
}

function myVehiclesSearch(searchStr) {
  const searchKeywords = searchStr.split(" ");
  const vehicles = myVehicleSearchList.metaobjects.nodes;

  let results = [];

  const nSearchStr = normaliseStr(searchStr);
  for (const v of vehicles) {
    const nHandle = normaliseStr(v.handle);
    const nMake = normaliseStr(v.make.value);
    const nModel = normaliseStr(v.model.value);
    const nClass = normaliseStr(v.class.value);

    // First, check if entire search term matches to potentially save having to
    // loop through keywords.
    if (nHandle.includes(nSearchStr) || nMake.includes(nSearchStr)
        || nModel.includes(nSearchStr) || nClass.includes(nSearchStr)) {
      results.push({ "handle": v.handle, "id": v.id, "make": v.make.value,
          "model": v.model.value, "class": v.class.value });
    } else {
      let keywordFound = false;
      // Now, loop through keywords and only accept vehicles that match every
      // keyword.
      for (const keyword of searchKeywords) {
        const nKeyword = normaliseStr(keyword);
        if (nHandle.includes(nKeyword) || nMake.includes(nKeyword)
            || nModel.includes(nKeyword) || nClass.includes(nKeyword)) {
          keywordFound = true;
        } else {
          keywordFound = false;
          break;
        }
      }
      if (keywordFound) {
        results.push({ "handle": v.handle, "id": v.id, "make": v.make.value,
            "model": v.model.value, "class": v.class.value });
      }
    }
  }
  
  return results;
}

/**
 * Calls public Shopify storefront API to get list of vehicles stored in metaobject.
 * This list is called once per page load and search is processed client-side.
 */
function populateVehicles() {
  fetch('https://trundlesautomotive.co.nz/api/2026-04/graphql.json', {
    method: 'POST',
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": "cd353d674b9c4d9b8f573c4232d7f94c"
    },
    body: JSON.stringify({
      query: `query Metaobjects {
        metaobjects(type: "vehicle", first: 250) {
          nodes {
            handle
            id
            make: field(key: "make") {
              value
            }
            model: field(key: "model") {
              value
            }
            class: field(key: "class") {
              value
            }
          }
        }
      }`,
    }),
  }).then(response => {
    response.json().then(json => {
      myVehicleSearchList = json.data
    });
  });
}

function defineMyVehicleSearch() {
  // Avoid double-definition.
  if (window.customElements.get('my-vehicle-search')) return;

  /**
   * This class is heavily based on PredictiveSearch.
   * It is basically a copy of that class but for the "Vehicle" metaobject
   * because Shopify doesn't index metaobjects for searching.
   */
  class MyVehicleSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.cachedResults = {};
      this.myVehicleSearchResults = this.querySelector('[data-my-vehicle-search]');
      this.isOpen = false;
      this.searchTerm = '';
      this.productTypes = this.querySelector('.js-product-types');
      window.initScriptOnDemand(this, this.init.bind(this));
    }

    init() {
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.input.addEventListener('input', debounce((event) => { this.onChange(event); }, 300).bind(this));
      this.productTypes?.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      this.open();
    }

    onFocus() {
      this.open();
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    onKeyup(event) {
      event.preventDefault();
      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up');
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');

      const allVisibleElements = Array.from(this.querySelectorAll('li')).filter(
        (element) => element.offsetParent !== null
      );
      let activeElementIndex = 0;

      if (moveUp && !selectedElement) {
        this.querySelector('[aria-selected="false"] li').setAttribute('aria-selected', true);
        return;
      }

      let selectedElementIndex = -1;
      let i = 0;

      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }

      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }

      if (activeElementIndex === selectedElementIndex) return;

      const activeElement = allVisibleElements[activeElementIndex];

      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);

      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"]');
      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      const myVehicleSearchBox = document.getElementById('My-Vehicle-Search');
      const results = myVehiclesSearch(myVehicleSearchBox.value);

      const outDiv = this.myVehicleSearchResults;
      let outStr = "<div id='my-vehicle-search__result'><ul>";
      for (const vehicle of results) {
        outStr += `<li id="${vehicle.id}" aria-selected="false">${vehicle.model}</li>`;
      }
      outStr += "</ul></div>";
      outDiv.innerHTML = outStr;
      // Add listeners for vehicle entries.
      outDiv.querySelectorAll("li").forEach(this.setMyVehicle, this);
      this.setAttribute('results', true);
    }

    setMyVehicle(li) {
      li.addEventListener("click", (event) => {
        localStorage.setItem("selected_vehicle_id", li.id);
        localStorage.setItem("selected_vehicle_model", li.textContent);
        // We reload the whole page so we don't just keep adding vehicles to
        // the URLs. This way we only add the currently selected vehicle.
        if (updateLinks()) location.reload();
        this.close(true);
      });
    }

    // Displays the selected vehicle in the call to action subtitle.
    displayVehicle() {
      if (localStorage.getItem("selected_vehicle_id")) {
        // 1. Set label
        document.querySelector("#cta_label").textContent = localStorage.getItem("selected_vehicle_model");
        // 2. Hide search box
        document.querySelector('#My-Vehicle-Search').classList.add("visually-hidden");
        // 3. Show clear button
        const clearButton = document.querySelector("#my-vehicle_clear");
        clearButton.classList.remove("visually-hidden");
        clearButton.addEventListener('click', (event) => {
          document.querySelector("#cta_label").textContent = document.querySelector("#cta_label_reference").textContent;
          localStorage.removeItem("selected_vehicle_id");
          document.querySelector("#my-vehicle_clear").classList.add("visually-hidden")
          document.querySelector('#My-Vehicle-Search').classList.remove("visually-hidden");
          location.reload();
        });
      }
    }

    open() {
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      this.getSearchResults(this.getQuery());
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
      }
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.removeAttribute('results');
      this.input.setAttribute('aria-expanded', false);
      document.querySelector('#my-vehicle-search__result')?.remove();
    }
  }

  window.customElements.define('my-vehicle-search', MyVehicleSearch);
}

function updateLinks() {
  let id = localStorage.getItem("selected_vehicle_id");
  if (!id) return false;
  // URL encoding for ":" and "/".
  id = id.replaceAll(":", "%3A");
  id = id.replaceAll("/", "%2F");
  document.querySelectorAll("a").forEach(anchor => {
    const link = anchor.href;
    if (link.startsWith(window.location.origin.toString()) && link.includes("/collections/")
        && (!anchor.innerHTML.toLowerCase().includes("clear filter") && !anchor.innerHTML.toLowerCase().includes("clear all"))) {
      let param = "";
      if (!link.includes("?")) {
        param = "?";
      } else {
        param = "&";
      }
      // Example of final param:
      // ?filter.p.m.rel.vehicles=gid%3A%2F%2Fshopify%2FMetaobject%2F159459672314
      param += `filter.p.m.rel.vehicles=${id}`;
      anchor.href += param;
    }
  });
  return true;
}

document.addEventListener("DOMContentLoaded", (event) => {
  populateVehicles();
  defineMyVehicleSearch();
  if (updateLinks()) {
    document.querySelector("my-vehicle-search").displayVehicle();
  }
  // Safety fallback: define predictive-search anyway after 6 seconds
  setTimeout(() => {
    if (!window.customElements.get('my-vehicle-search')) {
      defineMyVehicleSearch();
    }
  }, 6000);
});
